import { Link } from 'react-router';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

/**
 * In-app 404 page rendered for authenticated users hitting an unknown route.
 *
 * Unlike `NotFoundPage` (which is the public marketing 404 with its own header,
 * footer, and "Sign In" CTA), this component is rendered INSIDE
 * `AuthenticatedLayout`, so it inherits the sidebar nav and the user-avatar
 * topbar. It must therefore avoid duplicating any chrome and must point its
 * primary CTA at `/dashboard` rather than the public landing page.
 *
 * See issue #1765 — authenticated 404s previously rendered the marketing
 * shell, stripping sidebar/avatar and showing a misleading "Sign In" button.
 *
 * #3297 — this component previously wrapped everything in a MUI
 * `<Container maxWidth="md">`. `AuthenticatedLayout`'s `main-content` Box
 * already supplies the page's horizontal gutter; a fixed-width `Container`
 * nested inside that remaining width gets auto-centred instead of hugging
 * the layout's own padding, which pushed the heading ~74px right of every
 * other route's page title. Do not reintroduce a `Container` (or any other
 * horizontal wrapper) here — use the same bare `<Box className="page">`
 * structure every other authenticated page uses so the gutter always
 * matches, and see `tests/e2e/in-app-404-gutter.spec.ts` for the real-browser
 * geometry proof.
 */
function NotFoundInApp() {
    return (
        <Box className="page">
            <Box className="page-header">
                <Box>
                    <Typography variant="h5" component="h1">
                        404 &mdash; Page Not Found
                    </Typography>
                </Box>
            </Box>
            <Paper sx={{ p: { xs: 2, md: 4 } }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    The page you are looking for does not exist or has been moved.
                </Typography>
                <Box className="not-found-actions">
                    <Button component={Link} to="/dashboard" variant="contained" size="large" className="btn btn-primary btn-large">
                        Back to Dashboard
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
}

export default NotFoundInApp;
